import type { RootState } from '@/app/store'
import type { PageConfigRequest } from '@/types/api/page-config'
import type { OrderStatusGroup } from '@/types/enums/trade-blotter'

import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit'

import { updatePageConfig } from '@/legacy-api/page-config-slice'
import { defaultPageConfigs } from '@/reducers/defaults/page-configs'
import { Page } from '@/types/schema/page-configs'
import {
  TRADE_BLOTTER_SCHEMA_VERSION,
  type TradeBlotterConfig,
} from '@/types/schema/page-configs/trade-blotter'

interface TradeBlotterState {
  config: TradeBlotterConfig
  configVersion: string
  defaultConfig: TradeBlotterConfig
  orderStatusGroup: OrderStatusGroup | null
  searchTerm: string | null
  selectedOrder: string | null
  activeTabQuickFilter: OrderStatusGroup | null
}

export const initialState: TradeBlotterState = {
  config: defaultPageConfigs[Page.TradeBlotter],
  configVersion: TRADE_BLOTTER_SCHEMA_VERSION,
  defaultConfig: defaultPageConfigs[Page.TradeBlotter],
  orderStatusGroup: null,
  searchTerm: null,
  selectedOrder: null,
  activeTabQuickFilter: null,
}

const tradeBlotterSlice = createSlice({
  name: 'tradeBlotter',
  reducerPath: 'tradeBlotter',
  initialState,
  reducers: {
    initializeTradeBlotterConfig: handleInitializeTradeBlotterConfig,
    reset: handleReset,
    resetOrderStatusGroup: handleResetOrderStatusGroup,
    resetSearchTerm: handleResetSearchTerm,
    resetTradeBlotterConfig: handleResetTradeBlotterConfig,
    setOrderStatusGroup: handleSetOrderStatusGroup,
    setSearchTerm: handleSetSearchTerm,
    setTradeBlotterConfig: handleSetTradeBlotterConfig,
    setActiveTabQuickFilter: handleSetActiveTabQuickFilter,
  },
})

function handleReset(state: TradeBlotterState) {
  Object.assign(state, initialState)
}

function handleSetActiveTabQuickFilter(
  state: TradeBlotterState,
  action: PayloadAction<OrderStatusGroup>,
) {
  state.activeTabQuickFilter = action.payload
}

function handleInitializeTradeBlotterConfig(
  state: TradeBlotterState,
  action: PayloadAction<{ config: TradeBlotterConfig; version: string }>,
) {
  state.config = action.payload.config
  state.configVersion = action.payload.version
  // Override default config and version with the ones passed in from the API
  state.defaultConfig = action.payload.config
}

function handleResetTradeBlotterConfig(state: TradeBlotterState) {
  state.config = state.defaultConfig
}

function handleSetTradeBlotterConfig(
  state: TradeBlotterState,
  action: PayloadAction<Partial<TradeBlotterConfig>>,
) {
  const updatedConfig = {
    ...state.config,
    ...action.payload,
  }
  state.config = updatedConfig
}

function handleResetOrderStatusGroup(state: TradeBlotterState) {
  state.orderStatusGroup = null
}

function handleSetOrderStatusGroup(
  state: TradeBlotterState,
  action: PayloadAction<OrderStatusGroup>,
) {
  state.orderStatusGroup = action.payload
}

function handleSetSearchTerm(
  state: TradeBlotterState,
  action: PayloadAction<string>,
) {
  state.searchTerm = action.payload
}

function handleResetSearchTerm(state: TradeBlotterState) {
  state.searchTerm = null
}

// This allows us to invoke `await dispatch(persistTradeBlotterConfigAsyncThunk())` in component code so that
// the component is notified when the API request is complete -- something that isn't possible with listener
// middleware. This also ensure we have access to consistent config state when constructing the request.
export const persistTradeBlotterConfigAsyncThunk = createAsyncThunk(
  // biome-ignore lint/style/useTemplate: Bulk suppression as part of biome migration
  tradeBlotterSlice.reducerPath + '/persistTradeBlotterConfig',
  async (_data, { dispatch, getState, rejectWithValue }) => {
    const state = (getState() as RootState).tradeBlotter
    try {
      const request: PageConfigRequest = {
        id: Page.TradeBlotter,
        config: state.config,
        version: state.configVersion,
      }
      await dispatch(updatePageConfig.initiate(request))
    } catch (err) {
      const errorMessage =
        // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        (err as { response?: { data?: any } }).response?.data ||
        'An unknown error occurred'
      return rejectWithValue(errorMessage)
    }
  },
)

export const {
  initializeTradeBlotterConfig,
  reset,
  resetOrderStatusGroup,
  resetSearchTerm,
  resetTradeBlotterConfig,
  setOrderStatusGroup,
  setSearchTerm,
  setTradeBlotterConfig,
} = tradeBlotterSlice.actions

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default tradeBlotterSlice
